import { HttpErrorResponse } from '@angular/common/http';
import { Injector } from '@angular/core';

import { ApolloLink } from '@apollo/client/core';
import { onError } from '@apollo/client/link/error';

import { EnvironmentService, Logger, PlatformService, WindowRefService } from '@hdms/shared/util-core';

import { isGraphQLMothballerError, isGraphQLSessionExpiredError } from '../helper/graphql-utils';

const GQL_ERROR_LOG_PREFIX = '[GraphQL error]:';
const NETWORK_ERROR_LOG_PREFIX = '[Network error]:';

/**
 * Create an ErrorLink which intercepts and logs GraphQL/Network errors
 * @param injector
 */
export function createErrorLink(injector: Injector): ApolloLink {
  const { isBrowser } = injector.get(PlatformService);
  const { isStandalone } = injector.get(EnvironmentService);
  const { nativeWindow } = injector.get(WindowRefService);

  return onError(({ graphQLErrors, networkError }) => {
    const errorMessages = (graphQLErrors || []).map(error => {
      // in server log the full error but in client log only the message
      !isGraphQLSessionExpiredError(error.message) && logError(isBrowser, GQL_ERROR_LOG_PREFIX, error, error.message);
      return error.message;
    });

    if (isBrowser) {
      handleSessionExpiry(errorMessages, isStandalone, nativeWindow);
      handleMothballerError(networkError ? [JSON.stringify((networkError as HttpErrorResponse).error)] : errorMessages);
    }

    if (networkError) {
      logError(isBrowser, NETWORK_ERROR_LOG_PREFIX, networkError);
    }
  });
}

/**
 * In Hybris environment reload the page in order to refresh the session
 * @param errorMessages
 * @param isStandalone
 * @param win
 */
function handleSessionExpiry(errorMessages: string[], isStandalone: boolean, win: Window): void {
  if (!isStandalone && errorMessages.some(message => isGraphQLSessionExpiredError(message))) {
    win.location.reload();
  }
}

/**
 * Handles response errors caused by mothballed GraphQL endpoint on staging envs.
 * It will show a simple alert and disposes the router in order
 * to avoid annoying redirects to 404 or error pages
 * @param errorMessages
 * @constructor
 */
function handleMothballerError(errorMessages: string[]): void {
  const mothballerError = errorMessages.find(message => isGraphQLMothballerError(message));
  if (mothballerError) {
    alert(`${GQL_ERROR_LOG_PREFIX}\nEndpoint waking up. Please reload the page.\n\n${mothballerError}`);
  }
}

/**
 * Log errors depending on current platform
 * @param isBrowser
 * @param prefix
 * @param error
 * @param message
 */
function logError(isBrowser: boolean, prefix: string, error: Error, message?: string): void {
  isBrowser ? Logger.error(prefix, message || error) : Logger.error(error);
}
